// src/pages/laboratory/ContractsPage.jsx
import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  FileText,
  Search,
  Eye,
  Download,
  AlertCircle,
  Filter,
  SortAsc,
  SortDesc,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Hash,
  Timer,
  AlertTriangle,
  Tag
} from 'lucide-react';
import contractApi from '../../api/contract-api';
import { format, differenceInDays, isAfter, isBefore, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

// Import API functions
import {
  GET_LABORATORY_BY_USER,
  GET_CONTRACTS_BY_LABORATORY,
  GET_PROMOTIONS_BY_LABORATORY
} from '../../api/laboratory-api';
import { authApi } from '../../api/auth-api';

const ContractsPage = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Get current user data
  const userData = authApi.getUserData();
  const userId = userData?.id;

  // Fetch laboratory data for current user
  const { 
    data: laboratoryData, 
    loading: laboratoryLoading,
    error: laboratoryError 
  } = useQuery(GET_LABORATORY_BY_USER, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network'
  });

  const laboratory = laboratoryData?.laboratoryByUserId;

  // Fetch contracts for the laboratory
  const {
    data: contractsData,
    loading: contractsLoading,
    error: contractsError,
  } = useQuery(GET_CONTRACTS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id || '' },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network'
  });

  // Fetch promotions to show contract associations
  const {
    data: promotionsData,
    loading: promotionsLoading
  } = useQuery(GET_PROMOTIONS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id || '' },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network'
  });

  // Get contracts and promotions arrays
  const contracts = contractsData?.contractsByLaboratory || [];
  const promotions = promotionsData?.promotionsByLaboratory || [];

  // Create a map of contractId to promotions for efficient lookup
  const contractPromotionsMap = React.useMemo(() => {
    const map = new Map();
    
    if (promotions && promotions.length > 0) {
      promotions.forEach(promotion => {
        if (promotion.contratId) {
          if (!map.has(promotion.contratId)) {
            map.set(promotion.contratId, []);
          }
          map.get(promotion.contratId).push({
            id: promotion.id,
            name: promotion.name,
            status: promotion.status,
            startDate: promotion.startDate,
            endDate: promotion.endDate,
            commissionAmount: promotion.commissionAmount,
            commissionPercentage: promotion.commissionPercentage
          });
        }
      });
    }
    
    return map;
  }, [promotions]);

  // Helper functions
  const getContractStatus = (contract) => {
    const now = new Date();
    const startDate = new Date(contract.dateStart);
    const endDate = new Date(contract.dateEnd);
    const daysUntilExpiry = differenceInDays(endDate, now);

    if (isBefore(now, startDate)) {
      return {
        status: 'upcoming',
        text: 'À venir',
        className: 'bg-blue-100 text-blue-800',
        icon: Clock,
        description: `Débute le ${format(startDate, 'dd/MM/yyyy', { locale: fr })}`
      };
    } else if (isAfter(now, endDate)) {
      return {
        status: 'expired',
        text: 'Expiré',
        className: 'bg-red-100 text-red-800',
        icon: XCircle,
        description: `Expiré le ${format(endDate, 'dd/MM/yyyy', { locale: fr })}`
      };
    } else if (daysUntilExpiry <= 30) {
      return {
        status: 'expiring',
        text: 'Expire bientôt',
        className: 'bg-yellow-100 text-yellow-800',
        icon: AlertTriangle,
        description: `Expire dans ${daysUntilExpiry} jour${daysUntilExpiry > 1 ? 's' : ''}`
      };
    } else {
      return {
        status: 'active',
        text: 'Actif',
        className: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        description: `Expire le ${format(endDate, 'dd/MM/yyyy', { locale: fr })}`
      };
    }
  };

  const getContractDuration = (contract) => {
    const startDate = new Date(contract.dateStart);
    const endDate = new Date(contract.dateEnd);
    const totalDays = differenceInDays(endDate, startDate);
    
    if (totalDays < 30) {
      return `${totalDays} jour${totalDays > 1 ? 's' : ''}`;
    } else if (totalDays < 365) {
      const months = Math.floor(totalDays / 30);
      return `${months} mois`;
    } else {
      const years = Math.floor(totalDays / 365);
      const remainingMonths = Math.floor((totalDays % 365) / 30);
      return `${years} an${years > 1 ? 's' : ''}${remainingMonths > 0 ? ` ${remainingMonths} mois` : ''}`;
    }
  };

  const getContractStats = () => {
    const totalContracts = contracts.length;
    const activeContracts = contracts.filter(c => getContractStatus(c).status === 'active').length;
    const expiringContracts = contracts.filter(c => getContractStatus(c).status === 'expiring').length;
    const expiredContracts = contracts.filter(c => getContractStatus(c).status === 'expired').length;
    const contractsWithPromotions = contracts.filter(c => contractPromotionsMap.has(c.id)).length;

    return {
      totalContracts,
      activeContracts,
      expiringContracts,
      expiredContracts,
      contractsWithPromotions
    };
  };

  // Filter and sort contracts
  const filteredContracts = contracts
    .filter(contract => {
      const matchesSearch = contract.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (contract.nom && contract.nom.toLowerCase().includes(searchTerm.toLowerCase()));
      
      let matchesStatus = true;
      if (statusFilter !== 'all') {
        const contractStatus = getContractStatus(contract);
        matchesStatus = contractStatus.status === statusFilter;
      }
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'dateStart' || sortBy === 'dateEnd' || sortBy === 'createdAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      } else {
        return aVal > bVal ? 1 : -1;
      }
    });

  // Event handlers
  const handleDownloadContract = async (contract) => {
    if (!contract.piecesJointes) {
      toast.warning('Aucun fichier associé à ce contrat');
      return;
    }
    try {
      if (contract.piecesJointes.startsWith('http')) {
        const link = document.createElement('a');
        link.href = contract.piecesJointes;
        link.download = contract.number;
        link.click();
      } else {
        const filename = contract.piecesJointes.split('/').pop();
        await contractApi.downloadContract(filename);
      }
      toast.success('Téléchargement démarré');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handlePreviewContract = (contract) => {
    if (!contract.piecesJointes) {
      toast.warning('Aucun fichier associé à ce contrat');
      return;
    }
    if (contract.piecesJointes.startsWith('http')) {
      window.open(contract.piecesJointes, '_blank');
    } else {
      // Extract just the filename in case piecesJointes contains a path
      const filename = contract.piecesJointes.split('/').pop();
      contractApi.viewContract(filename);
    }
  };

  // Loading and error states
  if (laboratoryLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-700 border-r-transparent"></div>
        <p className="ml-3 text-gray-600">Chargement de votre laboratoire...</p>
      </div>
    );
  }

  if (laboratoryError || !laboratory) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Erreur</h3>
            <p className="text-red-700">
              {laboratoryError ? laboratoryError.message : 'Laboratoire non trouvé. Contactez l\'administrateur.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stats = getContractStats();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center min-w-0">
            <FileText className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold">Mes Contrats</h1>
              <p className="text-purple-100 text-sm hidden sm:block">
                Gérez vos contrats et accords - {laboratory.nom}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-purple-100">Total</p>
            <p className="text-xl font-bold">{stats.totalContracts}</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Actifs</p>
              <p className="text-2xl font-bold text-green-700">{stats.activeContracts}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Expirent bientôt</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.expiringContracts}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Expirés</p>
              <p className="text-2xl font-bold text-red-700">{stats.expiredContracts}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <Tag className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Avec promotions</p>
              <p className="text-2xl font-bold text-blue-700">{stats.contractsWithPromotions}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-purple-700">{stats.totalContracts}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4 lg:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              placeholder="Rechercher un contrat..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div className="relative w-full lg:w-48">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              className="block w-full pl-10 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="expiring">Expirent bientôt</option>
              <option value="expired">Expirés</option>
              <option value="upcoming">À venir</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex space-x-2">
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="createdAt">Date de création</option>
              <option value="dateStart">Date de début</option>
              <option value="dateEnd">Date de fin</option>
              <option value="number">Numéro</option>
              <option value="nom">Nom</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              title={`Trier par ordre ${sortOrder === 'asc' ? 'décroissant' : 'croissant'}`}
            >
              {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Contracts List */}
      {contractsLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-700 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Chargement des contrats...</p>
        </div>
      ) : filteredContracts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || statusFilter !== 'all' ? 'Aucun contrat trouvé' : 'Aucun contrat créé'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? "Modifiez vos critères de recherche pour voir plus de résultats."
              : "Commencez par créer votre premier contrat pour formaliser vos accords."}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contrat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Promotions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContracts.map((contract) => {
                  const statusInfo = getContractStatus(contract);
                  const StatusIcon = statusInfo.icon;
                  const contractPromotions = contractPromotionsMap.get(contract.id) || [];
                  
                  return (
                    <tr
                      key={contract.id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-5 w-5 text-purple-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {contract.nom || `Contrat ${contract.number}`}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Hash className="h-3 w-3 mr-1" />
                              {contract.number}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                            {format(new Date(contract.dateStart), 'dd/MM/yyyy', { locale: fr })}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                            {format(new Date(contract.dateEnd), 'dd/MM/yyyy', { locale: fr })}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Timer className="h-4 w-4 text-gray-400 mr-1" />
                          {getContractDuration(contract)}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <StatusIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.className}`}>
                            {statusInfo.text}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {statusInfo.description}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {contractPromotions.length > 0 ? (
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center">
                              <Tag className="h-4 w-4 text-gray-400 mr-1" />
                              <span>{contractPromotions.length} promotion{contractPromotions.length > 1 ? 's' : ''}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {contractPromotions.slice(0, 2).map(p => p.name).join(', ')}
                              {contractPromotions.length > 2 && `... +${contractPromotions.length - 2}`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Aucune</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Preview Contract */}
                          {contract.piecesJointes && (
                            <button
                              onClick={() => handlePreviewContract(contract)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Aperçu du contrat"
                            >
                              <Eye size={16} />
                            </button>
                          )}

                          {/* Download Contract */}
                          {contract.piecesJointes && (
                            <button
                              onClick={() => handleDownloadContract(contract)}
                              className="text-green-600 hover:text-green-900"
                              title="Télécharger le contrat"
                            >
                              <Download size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default ContractsPage;